import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface ContentItem {
  id: string;
  text: string;
  author: string;
  platform: string;
  topics?: string[];
  sentiment?: string;
  engagement?: {
    likes?: number;
    shares?: number;
    comments?: number;
  };
  created_at: string;
}

interface RecommendedContent extends ContentItem {
  recommendation_score: number;
  recommendation_reason: string;
}

interface UserInteraction {
  content_id: string;
  platform: string;
  interaction_type: 'view' | 'like' | 'share' | 'comment' | 'hide' | 'report';
  content_text?: string;
  content_author?: string;
  content_topics?: string[];
  content_sentiment?: string;
  interaction_value?: number;
}

interface RecommendationPreferences {
  preferred_topics: string[];
  blocked_topics: string[];
  preferred_authors: string[];
  blocked_authors: string[];
  sentiment_preference: 'positive' | 'negative' | 'neutral' | 'mixed';
  recommendation_frequency: number;
  content_freshness_weight: number;
  diversity_factor: number;
}

export const useSmartRecommendations = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generateRecommendations = useCallback(async (
    contentPool: ContentItem[],
    limit: number = 10
  ): Promise<RecommendedContent[]> => {
    if (!user) {
      setError('User not authenticated');
      return [];
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: functionError } = await supabase.functions.invoke('generate-recommendations', {
        body: {
          user_id: user.id,
          content_pool: contentPool,
          limit
        }
      });

      if (functionError) {
        throw new Error(functionError.message);
      }

      return data.recommendations || [];
    } catch (err: any) {
      setError(err.message || 'Failed to generate recommendations');
      return [];
    } finally {
      setLoading(false);
    }
  }, [user]);

  const trackInteraction = useCallback(async (interaction: UserInteraction): Promise<void> => {
    if (!user) return;

    try {
      // Determine interaction value based on type
      const interactionValues: Record<string, number> = {
        view: 0.1,
        like: 0.8,
        share: 1.0,
        comment: 0.9,
        hide: -0.5,
        report: -1.0
      };

      const { error } = await supabase
        .from('user_content_interactions')
        .insert({
          user_id: user.id,
          content_id: interaction.content_id,
          platform: interaction.platform,
          interaction_type: interaction.interaction_type,
          content_text: interaction.content_text,
          content_author: interaction.content_author,
          content_topics: interaction.content_topics,
          content_sentiment: interaction.content_sentiment,
          interaction_value: interaction.interaction_value ?? interactionValues[interaction.interaction_type]
        });

      if (error) {
        console.error('Failed to track interaction:', error);
      }
    } catch (err) {
      console.error('Error tracking interaction:', err);
    }
  }, [user]);

  const getRecommendationPreferences = useCallback(async (): Promise<RecommendationPreferences | null> => {
    if (!user) return null;

    try {
      const { data, error } = await supabase
        .from('user_recommendation_preferences')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') { // Not found error
        throw error;
      }

      return data as RecommendationPreferences;
    } catch (err: any) {
      setError(err.message || 'Failed to get preferences');
      return null;
    }
  }, [user]);

  const updateRecommendationPreferences = useCallback(async (
    preferences: Partial<RecommendationPreferences>
  ): Promise<void> => {
    if (!user) {
      setError('User not authenticated');
      return;
    }

    try {
      const { error } = await supabase
        .from('user_recommendation_preferences')
        .upsert({
          user_id: user.id,
          ...preferences
        });

      if (error) {
        throw error;
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update preferences');
    }
  }, [user]);

  const markRecommendationShown = useCallback(async (recommendationId: string): Promise<void> => {
    if (!user) return;

    try {
      await supabase
        .from('content_recommendations')
        .update({ is_shown: true })
        .eq('id', recommendationId)
        .eq('user_id', user.id);
    } catch (err) {
      console.error('Error marking recommendation as shown:', err);
    }
  }, [user]);

  const markRecommendationClicked = useCallback(async (recommendationId: string): Promise<void> => {
    if (!user) return;

    try {
      await supabase
        .from('content_recommendations')
        .update({ is_clicked: true })
        .eq('id', recommendationId)
        .eq('user_id', user.id);
    } catch (err) {
      console.error('Error marking recommendation as clicked:', err);
    }
  }, [user]);

  const dismissRecommendation = useCallback(async (recommendationId: string): Promise<void> => {
    if (!user) return;

    try {
      await supabase
        .from('content_recommendations')
        .update({ is_dismissed: true })
        .eq('id', recommendationId)
        .eq('user_id', user.id);
    } catch (err) {
      console.error('Error dismissing recommendation:', err);
    }
  }, [user]);

  const getRecommendationStats = useCallback(async (): Promise<{
    total_shown: number;
    total_clicked: number;
    click_through_rate: number;
    top_topics: string[];
  } | null> => {
    if (!user) return null;

    try {
      const { data: recommendations } = await supabase
        .from('content_recommendations')
        .select('*')
        .eq('user_id', user.id)
        .gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()); // Last 30 days

      if (!recommendations?.length) {
        return {
          total_shown: 0,
          total_clicked: 0,
          click_through_rate: 0,
          top_topics: []
        };
      }

      const shown = recommendations.filter(r => r.is_shown).length;
      const clicked = recommendations.filter(r => r.is_clicked).length;
      
      // Get top topics from clicked recommendations
      const topicsFromClicked = recommendations
        .filter(r => r.is_clicked && r.content_topics)
        .flatMap(r => r.content_topics);

      const topicCounts = topicsFromClicked.reduce((acc: Record<string, number>, topic: string) => {
        acc[topic] = (acc[topic] || 0) + 1;
        return acc;
      }, {});

      const topTopics = Object.entries(topicCounts)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 5)
        .map(([topic]) => topic);

      return {
        total_shown: shown,
        total_clicked: clicked,
        click_through_rate: shown > 0 ? clicked / shown : 0,
        top_topics: topTopics
      };
    } catch (err: any) {
      setError(err.message || 'Failed to get recommendation stats');
      return null;
    }
  }, [user]);

  return {
    generateRecommendations,
    trackInteraction,
    getRecommendationPreferences,
    updateRecommendationPreferences,
    markRecommendationShown,
    markRecommendationClicked,
    dismissRecommendation,
    getRecommendationStats,
    loading,
    error
  };
};